/**
 * UN-DOLLAR RENTFLOW — AUTHENTICATION CONTEXT
 * 
 * React Context for Firebase authentication with role-based access and permissions
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  Permission,
  UserRole,
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
  hasAllPermissions as checkAllPermissions,
  getPermissions,
  canAccessAdminDashboard,
  isAdminRole,
} from '../types/permissions';
import { updateLastLogin } from '../services/userManagement';

interface UserProfile {
  uid: string;
  email: string;
  role: UserRole;
  name?: string;
  permissions: Permission[];
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  canAccessDashboard: boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        // Fetch user profile from Firestore to get role and permissions
        try {
          // First try 'users' collection (new structure)
          let userDoc = await getDoc(doc(db, 'users', user.uid));
          
          // Fallback to 'landlords' collection (legacy)
          if (!userDoc.exists()) {
            userDoc = await getDoc(doc(db, 'landlords', user.uid));
          }
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            const role = (data.role || 'landlord') as UserRole;
            const permissions = getPermissions(role);
            
            // Extract name with fallback to email or uid
            const name = data.name || data.email || user.email || user.uid || 'Unknown User';
            
            console.log('✅ User profile loaded:', {
              uid: user.uid,
              email: user.email,
              role,
              name: name,
              permissionsCount: permissions.length,
              hasAdminAccess: role !== 'landlord',
              dataName: data.name,
              dataEmail: data.email
            });
            
            setUserProfile({
              uid: user.uid,
              email: user.email || '',
              role,
              name: name,
              permissions,
            });
            
            // Update last login timestamp
            updateLastLogin(user.uid).catch(console.error);
          } else {
            console.warn('⚠️ No user profile found in Firestore for:', user.uid);
            // Default to landlord role if no profile exists - provide name fallback
            const fallbackName = user.email || user.uid || 'Unknown User';
            setUserProfile({
              uid: user.uid,
              email: user.email || '',
              role: 'landlord',
              name: fallbackName,
              permissions: [],
            });
          }
        } catch (error) {
          console.error('❌ Error fetching user profile:', error);
          const firebaseError = error as any;
          console.error('Error details:', {
            code: firebaseError?.code,
            message: firebaseError?.message,
            uid: user.uid,
            email: user.email,
            errorType: firebaseError?.name,
            fullError: firebaseError
          });
          
          // Specific handling for permission denied
          if (firebaseError?.code === 'permission-denied') {
            console.warn('⚠️ FIRESTORE PERMISSION DENIED - Check security rules:');
            console.warn('  1. Verify users collection read permissions');
            console.warn('  2. Ensure admin() function includes sales and support roles');
            console.warn('  3. Check that user role is set correctly in Firestore');
          }
          
          // Try to fetch using different method to debug
          console.log('🔍 Attempting alternate fetch method...');
          try {
            const altDoc = await getDoc(doc(db, 'users', user.uid));
            console.log('Alternate fetch result:', { exists: altDoc.exists(), data: altDoc.data() });
          } catch (altError) {
            console.error('Alternate fetch also failed:', altError);
          }
          
          setUserProfile({
            uid: user.uid,
            email: user.email || '',
            role: 'landlord',
            name: user.email || user.uid || 'Unknown User',
            permissions: [],
          });
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const isAdmin = userProfile ? isAdminRole(userProfile.role) : false;
  const isSuperAdmin = userProfile?.role === 'superadmin';
  const canAccessDashboard = userProfile ? canAccessAdminDashboard(userProfile.role) : false;

  // Permission checking functions
  const hasPermissionCheck = (permission: Permission): boolean => {
    if (!userProfile) return false;
    return checkPermission(userProfile.role, permission);
  };

  const hasAnyPermissionCheck = (permissions: Permission[]): boolean => {
    if (!userProfile) return false;
    return checkAnyPermission(userProfile.role, permissions);
  };

  const hasAllPermissionsCheck = (permissions: Permission[]): boolean => {
    if (!userProfile) return false;
    return checkAllPermissions(userProfile.role, permissions);
  };

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      throw new Error(message);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to sign up';
      throw new Error(message);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      throw new Error(message);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        loading,
        isAdmin,
        isSuperAdmin,
        canAccessDashboard,
        hasPermission: hasPermissionCheck,
        hasAnyPermission: hasAnyPermissionCheck,
        hasAllPermissions: hasAllPermissionsCheck,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
